/**
 * (Shared since v0.133.0: the extension's gym helper and the website's calculators both use this.)
 *
 * Gym (in place of TornW3B's gym module and TornTools' gym helpers): estimated
 * gains per train and for all your energy, and which gym trains a stat best.
 * Pure. The gain estimate is the community's fitted formula (Vladar's, on Torn's
 * wiki) — close, not exact: Torn adds randomness, and happiness drops as you train.
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);

export const STATS = ['strength', 'speed', 'defense', 'dexterity'] as const;
export type Stat = (typeof STATS)[number];

export interface Gym {
  id: number;
  name: string;
  stage: number | null;
  /** Energy per train. */
  energy: number;
  /** Gym dots per stat (0 = can't train it there). */
  dots: Record<Stat, number>;
}

/** v1 /torn/?selections=gyms — Torn gives dots ×10 (e.g. 73 = 7.3). */
export function readGyms(body: unknown): Gym[] {
  return Object.entries(obj(obj(body)?.gyms) ?? {})
    .map(([k, v]): Gym | null => {
      const g = obj(v);
      const id = Number(k);
      const energy = num(g?.energy);
      if (!g || !Number.isInteger(id) || !energy) return null;
      const d = (x: unknown) => (num(x) ?? 0) / 10;
      return { id, name: typeof g.name === 'string' ? g.name : `Gym ${id}`, stage: num(g.stage), energy, dots: { strength: d(g.strength), speed: d(g.speed), defense: d(g.defense), dexterity: d(g.dexterity) } };
    })
    .filter((g): g is Gym => !!g)
    .sort((a, b) => a.id - b.id);
}

const CONST: Record<Stat, [number, number]> = { strength: [1600, 1700], speed: [1600, 2000], dexterity: [1800, 1500], defense: [2100, -600] };
const r4 = (x: number) => Math.round(x * 1e4) / 1e4;

/** Stats past 50m count for less (the formula's soft cap). */
export function effectiveStat(s: number): number {
  return s <= 50_000_000 ? s : (s - 50_000_000) / (8.77635 * Math.log(s)) + 50_000_000;
}

/** Estimated gain from one train. `bonus` is your gym-gain perks as a fraction (0.1 = +10%). */
export function trainGain(stat: Stat, value: number, happy: number, dots: number, energy: number, bonus = 0): number {
  if (dots <= 0 || energy <= 0) return 0;
  const [a, b] = CONST[stat];
  const s = effectiveStat(Math.max(0, value));
  const h = Math.max(0, happy);
  const core = s * r4(1 + 0.07 * r4(Math.log(1 + h / 250))) + 8 * h ** 1.05 + (1 - (h / 99_999) ** 2) * a + b;
  return Math.max(0, (core / 200_000) * dots * energy * (1 + bonus));
}

/** Gains for `trains` trains in a row; happiness drops by about half the energy each train. */
export function gainFor(stat: Stat, value: number, happy: number, dots: number, energy: number, trains: number, bonus = 0): number {
  let v = value;
  let h = happy;
  let total = 0;
  for (let i = 0; i < Math.min(trains, 10_000); i++) {
    const g = trainGain(stat, v, h, dots, energy, bonus);
    total += g;
    v += g;
    h = Math.max(0, h - energy / 2);
  }
  return total;
}

/**
 * Your gym-gain perks, overall and per stat, as fractions — from perk lines like
 * "+ 3% gym gains" or "+ 2% strength gym gains" (any source: faction, education, job…).
 */
export function gymBonus(perks: unknown): Record<Stat, number> {
  const out: Record<Stat, number> = { strength: 0, speed: 0, defense: 0, dexterity: 0 };
  const lines: string[] = [];
  const walk = (x: unknown) => {
    if (typeof x === 'string') lines.push(x);
    else if (Array.isArray(x)) x.forEach(walk);
    else if (obj(x)) Object.values(obj(x)!).forEach(walk);
  };
  walk(perks);
  for (const line of lines) {
    // "+ 3% strength gym gains" or "Increases strength gym gains by 3%".
    const a = /\+\s*([\d.]+)%\s*(strength|speed|defen[cs]e|dexterity)?\s*gym gains?/i.exec(line);
    const b = a ? null : /increases?\s+(?:your\s+)?(strength|speed|defen[cs]e|dexterity)?\s*gym gains?\s+by\s+([\d.]+)%/i.exec(line);
    if (!a && !b) continue;
    const pct = Number(a ? a[1] : b![2]) / 100;
    const which = (a ? a[2] : b![1])?.toLowerCase().replace('defence', 'defense') as Stat | undefined;
    for (const s of STATS) if (!which || which === s) out[s] += pct;
  }
  return out;
}

/** The gym that trains `stat` best among `gyms` (ties: less energy per train). */
export function bestGym(stat: Stat, gyms: readonly Gym[]): Gym | null {
  return gyms.filter((g) => g.dots[stat] > 0).sort((a, b) => b.dots[stat] - a.dots[stat] || a.energy - b.energy)[0] ?? null;
}

/** Compact amounts: 1,234 / 12.3k / 4.56m. */
export function compact(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}b`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)}m`;
  if (a >= 1e4) return `${(n / 1e3).toFixed(1)}k`;
  return Math.round(n).toLocaleString('en-US');
}

export type GymStats = { strength: number; speed: number; dexterity: number; defense: number };
/** Torn's specialist gyms: which you qualify for (25% rules) and how far off the rest are. */
export function specialistGyms(st: GymStats): Array<{ name: string; ok: boolean; short: number }> {
  const out: Array<{ name: string; ok: boolean; short: number }> = [];
  const pair = (name: string, a: number, b: number) => out.push({ name, ok: a >= b * 1.25, short: Math.max(0, Math.ceil(b * 1.25 - a)) });
  pair('Balboas Gym', st.defense + st.dexterity, st.strength + st.speed);
  pair('Frontline Fitness', st.strength + st.speed, st.defense + st.dexterity);
  const single = (name: string, k: keyof GymStats) => {
    const second = Math.max(...(Object.keys(st) as Array<keyof GymStats>).filter((x) => x !== k).map((x) => st[x]));
    pair(name, st[k], second);
  };
  single('Gym 3000', 'strength');
  single('Mr. Isoyamas', 'defense');
  single('Total Rebound', 'speed');
  single('Elites', 'dexterity');
  return out;
}


/** A gym's name as it's compared (Torn writes "Mr. Isoyamas", "George's"…). */
export const gymKey = (n: string): string => n.toLowerCase().replace(/[^a-z0-9]/g, '');
const SPECIALIST_KEYS = new Set(['Balboas Gym', 'Frontline Fitness', 'Gym 3000', 'Mr. Isoyamas', 'Total Rebound', 'Elites'].map(gymKey));
export const isSpecialist = (g: { name: string }): boolean => SPECIALIST_KEYS.has(gymKey(g.name));

/** Gyms outside the regular ladder that aren't specialist gyms either (The Sports Science Lab, Torn's "Unknown"). */
const OTHER_KEYS = new Set(['The Sports Science Lab', 'Unknown'].map(gymKey));
const isRegular = (g: { name: string }): boolean => !isSpecialist(g) && !OTHER_KEYS.has(gymKey(g.name));

/**
 * The gyms you can train at: the regular ones up to your current gym's stage, plus the specialist gyms your stats
 * qualify for once you've reached the last regular stage (report 174518-d3d4: Gym 3000 ✓ wasn't counted, so George's
 * looked best for strength). `open`, when the gym page is on screen: Torn's own gym list — name → usable (not
 * "locked") — which wins over the guess (report 204229-71c2: The Sports Science Lab's stage made George's look like
 * it wasn't the top, so Gym 3000 was never offered).
 */
export function reachableGyms(gyms: readonly Gym[], activeId: number | null, stats: GymStats | null, open?: ReadonlyMap<string, boolean>): Gym[] {
  const here = gyms.find((g) => g.id === activeId) ?? null;
  const regular = gyms.filter(isRegular);
  const stage = here && isRegular(here) ? here.stage : null;
  const maxStage = Math.max(0, ...regular.map((g) => g.stage ?? 0));
  const reach = stage !== null ? regular.filter((g) => g.stage !== null && g.stage <= stage) : here && !isRegular(here) ? [...regular] : [...gyms];
  const atTop = (here && isSpecialist(here)) || (stage !== null && stage >= maxStage);
  if (atTop && stats) {
    const ok = new Set(specialistGyms(stats).filter((x) => x.ok).map((x) => gymKey(x.name)));
    for (const g of gyms) if (isSpecialist(g) && (ok.has(gymKey(g.name)) || g.id === activeId) && !reach.includes(g)) reach.push(g);
  }
  if (here && !reach.includes(here)) reach.push(here);
  if (!open?.size) return reach;
  const out = reach.filter((g) => open.get(gymKey(g.name)) !== false);
  for (const g of gyms) if (open.get(gymKey(g.name)) === true && !out.includes(g)) out.push(g);
  return out.sort((a, b) => a.id - b.id);
}

// ---------------------------------------------------------------- website calculators (gym gains, happy jumps)

/** The gym formula's top happiness (its (happy ÷ 99,999)² term). */
export const HAPPY_MAX = 99_999;
/** Xanax: +250 energy (Torn's item description). */
export const XANAX_ENERGY = 250;
/** Hours a Xanax's drug cooldown lasts, roughly (players report 6–8 h; Torn picks it at random). */
export const XANAX_COOLDOWN_HOURS: readonly [number, number] = [6, 8];
/** Players report that energy items stop working above about this much energy — not confirmed by Torn. */
export const ENERGY_STACK_CAP = 1000;

export interface GymPlanInput {
  stat: Stat;
  /** The stat now. */
  value: number;
  happy: number;
  /** The gym's dots for that stat (e.g. 7.3). */
  dots: number;
  /** Energy per train at that gym. */
  energyPerTrain: number;
  /** Energy to spend. */
  energy: number;
  /** Gym-gain perks as a percentage (10 = +10%). */
  bonusPct?: number;
}

export interface GymPlan {
  trains: number;
  /** The first train's gain. */
  perTrain: number;
  /** All the trains', happiness dropping as you go. */
  total: number;
  /** Per 100 energy spent (null: nothing spent). */
  per100: number | null;
  /** Energy left over (less than one train). */
  left: number;
}

/** What `energy` spent at one gym gives (the community's gym formula — an estimate, see the top of this file). */
export function gymPlan(i: GymPlanInput): GymPlan {
  const e = Math.max(1, Math.floor(i.energyPerTrain || 0));
  const energy = Math.max(0, Math.floor(i.energy || 0));
  const trains = Math.min(10_000, Math.floor(energy / e));
  const bonus = (i.bonusPct ?? 0) / 100;
  const happy = Math.min(HAPPY_MAX, Math.max(0, i.happy || 0));
  const perTrain = trainGain(i.stat, i.value, happy, i.dots, e, bonus);
  const total = gainFor(i.stat, i.value, happy, i.dots, e, trains, bonus);
  return { trains, perTrain, total, per100: trains ? (total / (trains * e)) * 100 : null, left: energy - trains * e };
}

export interface HappyJumpInput extends Omit<GymPlanInput, 'energy'> {
  /** Happiness added before training (Erotic DVDs, candy, a Feathery Hotel Coupon… as Torn's items say). */
  boost: number;
  /** Ecstasy last: doubles happiness. */
  ecstasy: boolean;
  /** Xanax taken while stacking (+250 energy each). */
  xanax: number;
  /** Energy before the Xanax (your bar when you start, e.g. full). */
  energyStart: number;
  /** Any other energy added (cans, a refill, a Feathery Hotel Coupon's energy…). */
  energyExtra: number;
}

export interface HappyJump {
  energy: number;
  /** Happiness when the training starts. */
  happy: number;
  jump: GymPlan;
  /** The same energy trained at your everyday happiness. */
  normal: GymPlan;
  /** What the jump adds over training normally. */
  extra: number;
  /** Hours stacking the Xanax takes (their cooldowns back to back), low–high. */
  stackHours: [number, number];
  /** Above ENERGY_STACK_CAP (players say energy items stop working there). */
  overCap: boolean;
}

/** A happy jump: stack energy, raise happiness (boosters, then Ecstasy doubles it), train it all — against training it at your normal happiness. */
export function happyJump(i: HappyJumpInput): HappyJump {
  const xanax = Math.max(0, Math.min(20, Math.floor(i.xanax || 0)));
  const energy = Math.max(0, Math.floor(i.energyStart || 0)) + xanax * XANAX_ENERGY + Math.max(0, Math.floor(i.energyExtra || 0));
  const raised = Math.max(0, i.happy || 0) + Math.max(0, i.boost || 0);
  const happy = Math.min(HAPPY_MAX, i.ecstasy ? raised * 2 : raised);
  const jump = gymPlan({ ...i, happy, energy });
  const normal = gymPlan({ ...i, energy });
  // Each Xanax after the first waits for the last one's cooldown; the last one is taken right before training.
  const waits = Math.max(0, xanax - 1);
  return { energy, happy, jump, normal, extra: jump.total - normal.total, stackHours: [waits * XANAX_COOLDOWN_HOURS[0], waits * XANAX_COOLDOWN_HOURS[1]], overCap: energy > ENERGY_STACK_CAP };
}
